// What the carrier will accept as a caller-ID name, in one place.
//
// Commio has TWO SHAPES of caller-ID record and which one applies is not the operator's
// choice: a residential record needs a first AND a last name, a business one needs a single
// business name, and the caller-ID type has to match the number's 911 site type (Kevin,
// 2026-08-04). Fifteen characters because that is the field CNAM is delivered in; carriers
// truncate the rest.
//
// It lives here because there are now two editors — the caller-ID card on the DID page, and
// the line's settings sheet, which is the ONE gear on the line's Overview card (Kevin,
// 2026-08-19). Two copies of "residential means two fields, business means one, 15 characters
// either way" is how those two would come to disagree, and the disagreement would surface as
// a carrier refusal on a screen that thought the value was fine.

export const CNAM_MAX = 15;

// The recorded name, split the way the carrier holds it. Seeding only: an operator can
// correct a split this gets wrong, which is the point of asking for the parts separately.
export function splitCnam(cnam) {
  const parts = (cnam || '').trim().split(/\s+/).filter(Boolean);
  return { first: parts.slice(0, -1).join(' '), last: parts.slice(-1)[0] || '' };
}

// `residential` | `business` | anything else. Null/unknown asks for the single-name shape,
// which is the one that cannot be wrong about a field it never collected.
export function isResidentialCnam(cnamType) {
  return cnamType === 'residential';
}

/**
 * A plain "First Last" that will not fit, shortened to "F Last".
 *
 * Kevin, 2026-08-20: "fix caller id to be first initial, last name in the cases where it exceeds
 * the 15 character limit." Conrad Carpenter was stored as `Conrad Carpent` and Cindy Mahler-Mor as
 * `Cindy Mahler-Mo` — cut mid-surname, which is the half that identifies somebody.
 *
 * ONLY A PLAIN TWO-PART NAME. Of the eight residential names over the ceiling, five must be left
 * exactly as they are, and applying this blindly would have made four of them worse:
 *
 *   "Irene Rivas / Joseph Yurkovic" → "I Yurkovic"   two people; the first name is the right answer
 *   "Rhonda & David Long"          → "R Long"        a couple, already sensible as "Rhonda Long"
 *   "Redstone Cyclery"             → "R Cyclery"     a business typed residential
 *
 * So a name carrying `/`, `&`, or more than two words is left to the word-boundary fit. This
 * function answers one narrow question and says nothing when it does not apply.
 *
 * @returns {{first: string, last: string} | null} null when the rule does not apply
 */
export function initialAndSurname(fullName) {
  const clean = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!clean || /[/&]/.test(clean)) return null;
  const parts = clean.split(' ');
  if (parts.length !== 2) return null;
  const [first, last] = parts;
  // Nothing to do when the whole thing fits.
  if (`${first} ${last}`.length <= CNAM_MAX) return null;
  const short = `${first.charAt(0)} ${last}`;
  // A surname long enough to blow the ceiling on its own is a different problem, and an initial
  // does not solve it — leave that to the fit rather than returning something still too long.
  if (short.length > CNAM_MAX) return null;
  return { first: first.charAt(0), last };
}

// The three answers every caller-ID editor needs about what has been typed: what goes on the
// wire, what the carrier will show, and whether it is complete enough to send.
/**
 * What to prefill a residential editor with, given the name we want to present.
 *
 * Prefers the whole name, falls back to "F Last" when it will not fit, and otherwise leaves the
 * word-boundary fit to whoever is typing. Used for SEEDING only — never to rewrite a stored value
 * behind an operator's back, because the rule cannot tell a surname from a business:
 * "Redstone Cyclery" looks exactly like "Conrad Carpenter" to a string.
 */
export function seedResidentialCnam(stored, fullName = null) {
  const held = String(stored || '').trim();
  const full = String(fullName || '').trim();

  // ONLY WHEN WHAT IS STORED IS A CUT-OFF OF THE REAL NAME. `Conrad Carpent` is a prefix of
  // "Conrad Carpenter" and is not a name; that is the case worth proposing a fix for.
  //
  // Everything else is left exactly as it is. An EMPTY caller ID stays empty — prefilling it from
  // the customer's name would put a name on the wire that nobody chose, and "no caller ID recorded"
  // is a real state this product reports on purpose.
  const truncated = held && full && full.length > CNAM_MAX
    && full.startsWith(held) && held.length < full.length;
  if (truncated) {
    const short = initialAndSurname(full);
    if (short) return { first: short.first, last: short.last, shortened: true };
  }
  const parts = splitCnam(held);
  return { first: parts.first, last: parts.last, shortened: false };
}

export function cnamState(cnamType, { typed = '', first = '', last = '' }) {
  const residential = isResidentialCnam(cnamType);
  const t = typed.trim();
  const f = first.trim();
  const l = last.trim();
  const combined = residential ? [f, l].filter(Boolean).join(' ') : t;
  return {
    residential,
    payload: residential ? { first_name: f, last_name: l } : { name: t },
    combined,
    // Residential needs BOTH parts — the server used to guess the split from one box, which
    // turns "Mahoney" into a record with no first name.
    complete: residential ? Boolean(f && l) : Boolean(t),
    tooLong: combined.length > CNAM_MAX,
    remaining: CNAM_MAX - combined.length,
  };
}

// The sentence under the field. Says which shape this number is and why, because the form
// asking for one name on one line and two on another is otherwise unexplained.
export function cnamShapeHint(cnamType) {
  if (isResidentialCnam(cnamType)) return 'This is a residential number at the carrier, so it needs both names.';
  if (cnamType === 'business') return 'This is a business number at the carrier, so it takes one business name.';
  return 'A business name, or a first and last name for a residential number.';
}
